/**
 * ProtectedRoute – v9 (Simplified & Stable)
 *
 * LOGIC ĐƠN GIẢN – 4 trường hợp:
 * ─────────────────────────────────
 * 1. isLoading=true → FullScreenLoader (auth đang init)
 *    AuthContext safety timer 5s sẽ thoát nếu bị kẹt.
 *
 * 2. authError có giá trị → Navigate /auth/login với error message
 *    (tài khoản bị khóa, profile fail, session invalid)
 *
 * 3. !isAuthenticated → Navigate /auth/login
 *    (chưa đăng nhập hoặc session hết hạn)
 *
 * 4. isAuthenticated + profile → Render children ✅
 *
 * KHÔNG có:
 * - Profile timeout riêng (AuthContext đã có safety timer)
 * - signOut() trong guard (gây event loop)
 * - Nhiều timer chồng nhau
 * - Case "authenticated + no profile" → FullScreenLoader mãi
 *   (v9: nếu authenticated nhưng profile=null → authError đã được set bởi AuthContext)
 */
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authNavigateRef } from './AuthContext';
import { brandConfig } from '@/config/brand.config';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function FullScreenLoader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
      <div className="relative">
        <div className="w-14 h-14 flex items-center justify-center bg-green-600 rounded-2xl">
          <i className={`${brandConfig.logo.icon} text-white text-2xl`} />
        </div>
        <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center bg-white rounded-full border-2 border-green-100">
          <i className="ri-loader-4-line animate-spin text-green-600 text-xs" />
        </div>
      </div>
    </div>
  );
}

/** Inject navigate vào authNavigateRef để AuthContext dùng khi signOut/clearInvalidSession */
function NavigateInjector() {
  const navigate = useNavigate();
  useEffect(() => {
    authNavigateRef.current = navigate;
  }, [navigate]);
  return null;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, authError, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Inject navigate ref sớm nhất có thể
  useEffect(() => {
    authNavigateRef.current = navigate;
  }, [navigate]);

  // 1. Auth đang init
  if (isLoading) {
    return (
      <>
        <NavigateInjector />
        <FullScreenLoader />
      </>
    );
  }

  // 2. authError (tài khoản bị khóa, profile fail, session invalid)
  if (authError) {
    return <Navigate to="/auth/login" state={{ from: location, error: authError }} replace />;
  }

  // 3. Chưa authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // 4. Authenticated + profile ready → render
  // Nếu profile=null nhưng isAuthenticated=true → AuthContext đang fetch profile
  // AuthContext sẽ set authError nếu fetch fail → case 2 sẽ handle
  // Nếu vẫn đang fetch → isLoading=true → case 1 handle
  // Không bao giờ stuck ở đây
  if (!profile) {
    // Profile đang fetch (isLoading đã false nhưng profile chưa có)
    // Trường hợp này rất hiếm – AuthContext thường set isLoading=false sau khi có profile
    return (
      <>
        <NavigateInjector />
        <FullScreenLoader />
      </>
    );
  }

  return (
    <>
      <NavigateInjector />
      {children}
    </>
  );
}
